import { memo, useMemo } from "react";
import { getOrderVerb, squadColor, type OrderKind } from "@/data/squads.ts";
import type { GameCoord } from "@/engine/coords.ts";
import { fromGridRef, type GridConfig } from "@/engine/grid.ts";
import { gameToScreen, type Camera, type ProjectionConfig } from "@/engine/projection.ts";

/**
 * A squad's tasking, as the map sees it.
 *
 * Kept deliberately free of session types so the map never has to know what a
 * session is — it draws what it is handed.
 */
export interface OrderMarker {
  readonly id: string;
  readonly squadId: string;
  readonly squadTag: string;
  readonly kind: OrderKind;
  /** Explicit coordinate, when the order carries one. */
  readonly at?: GameCoord | undefined;
  /** Callout reference, resolved to a coordinate when `at` is absent. */
  readonly place: string;
}

interface OrderLayerProps {
  readonly orders: readonly OrderMarker[];
  readonly grid: GridConfig;
  readonly camera: Camera;
  readonly config: ProjectionConfig;
  /**
   * Present only for the commander: a tap on a pin withdraws that order,
   * the same one-tap rule markers and routes already follow.
   */
  readonly onRemove?: ((orderId: string) => void) | undefined;
}

/**
 * Orders drawn on the map.
 *
 * The point is that a commander should be able to read the whole plan without
 * reading any text in a sidebar. So each active order becomes a squad-coloured
 * pin on its target, labelled with the verb.
 */
function OrderLayerImpl({
  orders,
  grid,
  camera,
  config,
  onRemove,
}: OrderLayerProps): React.ReactElement {
  const placed = useMemo(() => {
    const result: Array<{
      order: OrderMarker;
      at: { x: number; y: number };
      label: string;
    }> = [];

    for (const order of orders) {
      // An explicit coordinate always wins; otherwise resolve the callout.
      const coord = order.at ?? (order.place ? fromGridRef(order.place, grid) : undefined);
      if (!coord) continue;

      result.push({
        order,
        at: gameToScreen(coord, camera, config),
        label: getOrderVerb(order.kind)?.imperative ?? order.kind.toUpperCase(),
      });
    }

    return result;
  }, [orders, grid, camera, config]);

  return (
    <svg className="pointer-events-none absolute inset-0 h-full w-full">
      {placed.map(({ order, at, label }) => {
        const color = squadColor(order.squadId);

        return (
          <g
            key={order.id}
            className={onRemove ? "pointer-events-auto cursor-pointer" : undefined}
            // Keep the map's pan gesture off a press that starts on the pin,
            // or pointer capture swallows the click — see `useCamera`.
            data-no-pan={onRemove ? "" : undefined}
            onClick={
              onRemove
                ? (event) => {
                    event.stopPropagation();
                    onRemove(order.id);
                  }
                : undefined
            }
            role={onRemove ? "button" : undefined}
            aria-label={onRemove ? `Withdraw ${label} order` : undefined}
          >
            {onRemove && <title>Click to withdraw this order</title>}

            {/* Generous invisible hit area, same as markers. */}
            {onRemove && <circle cx={at.x} cy={at.y} r={24} fill="transparent" />}

            <circle
              cx={at.x}
              cy={at.y}
              r={16}
              fill="none"
              stroke={color}
              strokeWidth={2}
              opacity={0.9}
            />

            <circle
              cx={at.x}
              cy={at.y}
              r={10}
              fill={color}
              stroke="oklch(0.12 0.008 250)"
              strokeWidth={2}
            />

            <text
              x={at.x}
              y={at.y}
              textAnchor="middle"
              dominantBaseline="central"
              fontSize={11}
              fontWeight={700}
              fontFamily="var(--font-mono)"
              fill="oklch(0.16 0.01 250)"
            >
              {order.squadTag}
            </text>

            <text
              x={at.x}
              y={at.y + 30}
              textAnchor="middle"
              fontSize={11}
              fontWeight={700}
              fontFamily="var(--font-mono)"
              stroke="oklch(0.12 0.008 250)"
              strokeWidth={3.5}
              strokeLinejoin="round"
              paintOrder="stroke"
              fill={color}
            >
              {label}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

export const OrderLayer = memo(OrderLayerImpl);
